import { useEffect, useState } from "react";
import { shopping as api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Trash2, ShoppingCart, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { phase3Api } from "../lib/phase3Api";
import { isFeatureEnabled } from "../components/FeatureGate";

export default function ShoppingPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [qty, setQty] = useState("1");
  const [category, setCategory] = useState("General");
  const [frequent, setFrequent] = useState([]);

  async function load() {
    setLoading(true);
    try { setList(await api.list()); } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  }
  async function loadFrequent() {
    if (!isFeatureEnabled("feature_frequently_bought")) return;
    try { setFrequent(await phase3Api.frequentlyBought(8)); } catch { /* noop */ }
  }
  useEffect(() => { load(); loadFrequent(); }, []);

  async function add() {
    if (!name.trim()) return;
    try {
      await api.create({ name, quantity: qty, category });
      setName(""); setQty("1");
      load();
    } catch { toast.error("Add failed"); }
  }

  async function quickAdd(item) {
    try {
      await api.create({ name: item.name, quantity: "1", category: item.category || "General" });
      load();
    } catch { toast.error("Couldn't add"); }
  }

  async function toggle(t) {
    try { await api.update(t.id, { purchased: !t.purchased }); load(); loadFrequent(); } catch { toast.error("Update failed"); }
  }

  async function remove(id) {
    try { await api.remove(id); load(); } catch { toast.error("Delete failed"); }
  }

  const grouped = list.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="animate-fade-up">
      <div className="mb-8">
        <span className="label-caps">Utility</span>
        <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">Shopping List.</h1>
        <p className="mt-2 text-[color:var(--text-muted)] max-w-xl">Running list, grouped by aisle. Check items off as you go.</p>
      </div>

      <div className="card-tactile rounded-2xl p-5 mb-6 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-6">
          <label className="label-caps mb-1 block">Item</label>
          <Input data-testid="shop-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Oat milk" onKeyDown={(e) => e.key === "Enter" && add()} />
        </div>
        <div className="md:col-span-2">
          <label className="label-caps mb-1 block">Qty</label>
          <Input data-testid="shop-qty" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="1" />
        </div>
        <div className="md:col-span-3">
          <label className="label-caps mb-1 block">Aisle / Category</label>
          <Input data-testid="shop-category" value={category} onChange={(e) => setCategory(e.target.value)} />
        </div>
        <div className="md:col-span-1">
          <Button onClick={add} data-testid="shop-add" className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white w-full h-10">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {isFeatureEnabled("feature_frequently_bought") && frequent.length > 0 && (
        <div className="card-tactile rounded-2xl p-4 mb-6" data-testid="shop-frequent">
          <div className="label-caps mb-2 flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Frequently bought</div>
          <div className="flex flex-wrap gap-1.5">
            {frequent.map(f => (
              <button key={f.name} onClick={() => quickAdd(f)}
                data-testid={`freq-${f.name}`}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border hover:border-[color:var(--primary)] hover:bg-[color:var(--surface-2)] transition-colors"
                style={{ borderColor: "var(--border-subtle)" }}>
                <Plus className="h-3 w-3" /> {f.name}
                <span className="text-[10px] opacity-60">×{f.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? <div className="text-[color:var(--text-muted)]">Loading…</div> :
        list.length === 0 ? (
          <div className="card-tactile rounded-2xl p-10 flex flex-col items-center text-center" data-testid="empty-shopping">
            <div className="h-16 w-16 rounded-full bg-[color:var(--accent-soft)] flex items-center justify-center mb-4"><ShoppingCart className="h-7 w-7 text-[color:var(--accent-ink)]" /></div>
            <h3 className="font-display font-bold text-xl">Your list is empty</h3>
            <p className="text-sm text-[color:var(--text-muted)] mt-1">Start adding items above.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <h3 className="label-caps mb-3">{cat} · {items.length}</h3>
                <div className="space-y-2">
                  {items.map((t, i) => (
                    <div key={t.id} data-testid={`shop-row-${t.id}`} className="card-tactile rounded-xl p-4 flex items-center gap-3 animate-fade-up" style={{ animationDelay: `${i * 30}ms` }}>
                      <Checkbox checked={t.purchased} onCheckedChange={() => toggle(t)} data-testid={`shop-check-${t.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold ${t.purchased ? "line-through text-[color:var(--text-muted)]" : "text-[color:var(--text-main)]"}`}>{t.name}</div>
                      </div>
                      <span className="dose-pill pending">x{t.quantity}</span>
                      <button onClick={() => remove(t.id)} data-testid={`shop-delete-${t.id}`} className="text-[color:var(--text-muted)] hover:text-[color:var(--accent-ink)] p-1">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
